/*
 * eslint-disable @typescript-eslint/naming-convention
 *
 * Postgres 컬럼은 snake_case 이고 supabase-js 는 컬럼명을 그대로 키로 쓴다. 이 파일은 그 경계를
 * 넘는 곳(행 → 도메인 타입 변환)이라 snake_case 가 불가피하다. 규칙을 파일 단위로 끄되,
 * **변환 이후의 도메인 타입은 전부 camelCase** 라 앱 내부 규약은 그대로 유지된다.
 */
/* eslint-disable @typescript-eslint/naming-convention */
import { getAuthedClient, getSessionUserId, supabase } from '~/common/utils/supabase.util.ts';

import type {
  MyProfile,
  OnboardingResult,
  PetProfile,
} from '~/features/profile/types/profile.type';

/**
 * 프로필 데이터 어댑터 — Supabase 구현.
 * @description 대응 테이블은 `profiles`(보호자) · `pets`(반려견). 신원은 `auth.uid()` 이며
 *  RLS 가 본인 행만 통과시킨다(`supabase.util.ts` 의 계정 미러링 참조).
 *
 *  프로필과 반려견은 **한 번에** 만든다. 따로 저장하면 "반려견만 등록되고 보호자는 없는" 반쪽 상태가
 *  생겨 홈의 듀오 아바타가 깨진다. 트랜잭션 대신 프로필 → 반려견 순으로 쓰고, 조회 시 두 축이
 *  모두 있을 때만 완료로 판정해 반쪽 저장본을 미완료로 취급한다.
 */

/** 반려견 이름·닉네임 공통 형식 규칙 (시안 placeholder: 2~10자, 특수문자 불가). */
const NAME_PATTERN = /^[가-힣a-zA-Z0-9]{2,10}$/;

interface ProfileRow {
  id: string;
  nickname: string;
  avatar_url: string | null;
  role: MyProfile['role'];
  slug: string | null;
}

interface PetRow {
  id: string;
  name: string;
  photo_url: string | null;
  breed: string | null;
  sex: PetProfile['sex'] | null;
  anniversary: string | null;
  weight_range: string | null;
  neutered: PetProfile['neutered'] | null;
  health_note: string | null;
}

/** 현재 로그인 계정에 연결된 프로필 행. 없으면 null — 미로그인이거나 온보딩 미완료를 뜻한다. */
async function getMyProfileRow(): Promise<ProfileRow | null> {
  const client = await getAuthedClient();
  if (!client) return null;
  const uid = await getSessionUserId();
  if (!uid) return null;

  const { data, error } = await client
    .from('profiles')
    .select('id, nickname, avatar_url, role, slug')
    .eq('auth_user_id', uid)
    .maybeSingle();

  if (error) throw error;
  return (data as ProfileRow | null) ?? null;
}

/** 매칭 어댑터가 신청의 `guardian_id` 를 채울 때 쓴다. */
export async function getMyProfileId(): Promise<string | null> {
  const row = await getMyProfileRow();
  return row?.id ?? null;
}

/**
 * 내 프로필의 신원·역할.
 * @description 화면 분기(보호자 뷰 / 지도사 콘솔)와 채팅 좌우 버블 판정이 이 값을 쓴다.
 *
 *  ⚠️ **화면 분기는 편의일 뿐 보안이 아니다.** 역할을 클라이언트에서 확인하는 것만으로는
 *  아무것도 막지 못한다(응답을 조작하면 그만이다). 실제 차단은 RLS 가 한다 —
 *  지도사가 아닌 사람이 지도사용 데이터를 요청해도 DB 가 빈 결과를 준다.
 */
export async function getMyProfileApi(): Promise<MyProfile | null> {
  const row = await getMyProfileRow();
  if (!row) return null;
  return {
    id: row.id,
    nickname: row.nickname,
    role: row.role,
    slug: row.slug ?? undefined,
    avatarUrl: row.avatar_url ?? undefined,
  };
}

export async function fetchOnboardingApi(): Promise<OnboardingResult | null> {
  const client = await getAuthedClient();
  if (!client) return null;

  const profile = await getMyProfileRow();
  if (!profile) return null;

  const { data: pets, error } = await client
    .from('pets')
    .select('id, name, photo_url, breed, sex, anniversary, weight_range, neutered, health_note')
    .eq('owner_id', profile.id)
    .order('created_at', { ascending: true })
    .limit(1);

  if (error) throw error;

  const pet = (pets as PetRow[] | null)?.[0];
  // 두 축이 모두 있어야 완료 — 반쪽 저장본은 없는 것으로 보고 온보딩을 다시 태운다.
  if (!pet) return null;

  return {
    pet: {
      id: pet.id,
      name: pet.name,
      photoUrl: pet.photo_url ?? undefined,
      breed: pet.breed ?? undefined,
      sex: pet.sex ?? undefined,
      anniversary: pet.anniversary ?? undefined,
      weightRange: pet.weight_range ?? undefined,
      neutered: pet.neutered ?? undefined,
      healthNote: pet.health_note ?? undefined,
    },
    guardian: { nickname: profile.nickname, photoUrl: profile.avatar_url ?? undefined },
    completedAt: new Date().toISOString(),
  };
}

export async function saveOnboardingApi(result: OnboardingResult): Promise<OnboardingResult> {
  const client = await getAuthedClient();
  if (!client) throw new Error('Supabase 세션을 만들 수 없습니다.');
  const uid = await getSessionUserId();
  if (!uid) throw new Error('Supabase 세션을 만들 수 없습니다.');

  // 재진입(프로필 수정)도 같은 경로를 타도록 upsert 한다 — auth_user_id 가 자연키다.
  const { data: profile, error: profileError } = await client
    .from('profiles')
    .upsert(
      {
        auth_user_id: uid,
        nickname: result.guardian.nickname,
        avatar_url: result.guardian.photoUrl ?? null,
        role: 'guardian',
      },
      { onConflict: 'auth_user_id' },
    )
    .select('id')
    .single();

  if (profileError) throw profileError;
  const ownerId = (profile as { id: string }).id;

  // 반려견은 온보딩당 1마리 — 기존 행이 있으면 갈아끼운다(다마리 지원은 별도 화면이 생길 때).
  const { error: deleteError } = await client.from('pets').delete().eq('owner_id', ownerId);
  if (deleteError) throw deleteError;

  const { data: pet, error: petError } = await client
    .from('pets')
    .insert({
      owner_id: ownerId,
      name: result.pet.name,
      photo_url: result.pet.photoUrl ?? null,
      breed: result.pet.breed ?? null,
      sex: result.pet.sex ?? null,
      anniversary: result.pet.anniversary ?? null,
      weight_range: result.pet.weightRange ?? null,
      neutered: result.pet.neutered ?? null,
      health_note: result.pet.healthNote ?? null,
    })
    .select('id')
    .single();

  if (petError) throw petError;

  return {
    ...result,
    pet: { ...result.pet, id: (pet as { id: string }).id },
    completedAt: new Date().toISOString(),
  };
}

/**
 * 반려견 이름 확인 (시안 1/6).
 * @description 형식 + **내 반려견들 사이의** 중복만 본다. 남의 반려견 이름은 RLS 가 가려 조회 자체가
 *  불가능하고, 애초에 반려견 이름이 전역 유일해야 할 이유도 없다(시안의 "중복"은 같은 보호자 기준으로 읽는다).
 */
export async function checkPetNameApi(name: string): Promise<boolean> {
  if (!NAME_PATTERN.test(name)) return false;

  const client = await getAuthedClient();
  const profile = client ? await getMyProfileRow() : null;
  if (!client || !profile) return true;

  const { data, error } = await client
    .from('pets')
    .select('id')
    .eq('owner_id', profile.id)
    .eq('name', name);

  if (error) throw error;
  return (data?.length ?? 0) === 0;
}

/**
 * 보호자 닉네임 중복 확인 (시안 5/6).
 * @description 닉네임은 `profiles.nickname` 이 unique 이고 profiles 의 SELECT 는 공개(추천 지도사 목록에
 *  필요)라 전역 중복 확인이 가능하다. 본인 닉네임은 중복으로 치지 않는다(수정 시 자기 값에 걸리는 것 방지).
 */
export async function checkNicknameApi(nickname: string): Promise<boolean> {
  if (!NAME_PATTERN.test(nickname)) return false;
  if (!supabase) return true;

  const client = await getAuthedClient();
  if (!client) return true;

  const mine = await getMyProfileRow();

  const { data, error } = await client.from('profiles').select('id').eq('nickname', nickname);
  if (error) throw error;

  const rows = (data as { id: string }[] | null) ?? [];
  return rows.every(row => row.id === mine?.id);
}
